import {
  createContext,
  forwardRef,
  useMemo,
  useImperativeHandle,
  useCallback,
  createRef,
  useState,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';

import Loading from 'components/Loading';
import TeamChanger from 'components/TeamChanger';

import {
  isDealerTeam as isDealerTeamType,
  isFleet as isFleetType,
  isDealerGroup as isDealerGroupType,
  isSingleLocation as isSingleLocationType,
} from 'utils/SentinelTeam';
import { useCurrentUserContext } from 'utils/User';

import { FetchResult, MutationFunctionOptions } from '@apollo/client';
import useCurrentSentinelTeamQuery from './useCurrentSentinelTeamQuery';
import useSetCurrentSentinelTeamMutation from './useSetCurrentSentinelTeamMutation';

type SetSentinelTeamMutationOpts = MutationFunctionOptions<
  SetCurrentSentinelTeamMutation,
  SetCurrentSentinelTeamMutationVariables
>;

type SetSentinelTeamType = (
  options?: SetSentinelTeamMutationOpts,
) => Promise<FetchResult<SetCurrentSentinelTeamMutation>> | Promise<void>;

interface SentinelTeamProviderProps {
  children: React.ReactNode;
}

interface SentinelTeamProviderHandles {
  setSentinelTeam: SetSentinelTeamType;
}

export interface SentinelTeamContextProps extends CurrentSentinelTeam {
  isFleet: boolean;
  isDealerTeam: boolean;
  isDealerGroup: boolean;
  isSingleLocation: boolean;
  isTeamMember: boolean;
  hasSentinelTeam: boolean;
  isTeamEnabled: boolean;
  belongsToOemSegment: boolean;
  setSentinelTeam: SetSentinelTeamType;
  logout: () => Promise<void>;
}

export const SentinelTeamContext = createContext<SentinelTeamContextProps>(
  {} as SentinelTeamContextProps,
);

const SentinelTeamProvider = forwardRef(
  (
    { children }: SentinelTeamProviderProps,
    ref: React.Ref<SentinelTeamProviderHandles>,
  ) => {
    const [isSettingTeam, setSettingTeam] = useState(false);

    const { sentinelTeams = [] } = useCurrentUserContext();

    const {
      data: {
        currentUserProfile: {
          user: { sentinelTeam = {} as CurrentSentinelTeam } = {},
        } = {},
      } = {},
      loading,
    } = useCurrentSentinelTeamQuery();

    const [updateSentinelTeam, { loading: setSentinelTeamLoading }] =
      useSetCurrentSentinelTeamMutation();

    const { teamType, id: teamId, enabled } = sentinelTeam || {};

    const isFleet = useMemo(() => isFleetType(teamType), [teamType]);
    const isDealerTeam = useMemo(
      () =>
        isDealerTeamType(teamType) ||
        isDealerGroupType(teamType) ||
        isSingleLocationType(teamType),
      [teamType],
    );

    const isDealerGroup = false;
    const isSingleLocation = false;
    const belongsToOemSegment = !!sentinelTeam?.oemSegment;

    const isTeamMember = useMemo(
      () => sentinelTeams.map(({ id }) => id).includes(teamId),
      [sentinelTeams, teamId],
    );

    const hasSentinelTeam = useMemo(() => !!teamId, [teamId]);

    const isTeamEnabled = useMemo(() => !!enabled, [enabled]);

    // Redirect to teamUrl if it's defined and host differs from current host.
    // Skip if only the scheme (http vs https) differs to avoid loops under forced HTTPS.
    // Skip entirely on Amplify PR preview domains (pattern: pr-<num>.<hash>.amplifyapp.com).
    useEffect(() => {
      const teamUrl = sentinelTeam?.teamUrl;

      if (teamUrl && !loading) {
        try {
          const url = new URL(teamUrl);
          const current = new URL(window.location.href);
          const amplifyPrPreview =
            /(^pr-\d+\.)[a-z0-9-]+\.amplifyapp\.com$/i.test(current.hostname);
          if (amplifyPrPreview) return;
          const targetHost = url.hostname.toLowerCase();
          const currentHost = current.hostname.toLowerCase();
          const schemeDiffOnly =
            targetHost === currentHost && url.protocol !== current.protocol;

          if (!schemeDiffOnly && targetHost !== currentHost) {
            const targetUrl = `${url.protocol}//${url.host}${window.location.pathname}${window.location.search}${window.location.hash}`;
            window.location.href = targetUrl;
          }
        } catch (error) {
          // Invalid URL, ignore and continue
        }
      }
    }, [sentinelTeam, loading]);

    const setCurrentSentinelTeam = useCallback(
      async (options) => {
        setSettingTeam(true);
        await updateSentinelTeam(options);
        window.location.href = '/';
      },
      [updateSentinelTeam],
    );

    const logout = useCallback(async () => {
      setSettingTeam(true);
      await updateSentinelTeam({
        variables: { id: null as unknown as any },
      });
      window.location.reload();
    }, [updateSentinelTeam]);

    useImperativeHandle(
      ref,
      () => ({
        setSentinelTeam: updateSentinelTeam,
      }),
      [updateSentinelTeam],
    );

    const values = useMemo(
      () => ({
        ...sentinelTeam,
        isFleet,
        isDealerTeam,
        isDealerGroup,
        isSingleLocation,
        isTeamMember,
        belongsToOemSegment,
        hasSentinelTeam,
        isTeamEnabled,
        setSentinelTeam: setCurrentSentinelTeam,
        logout,
      }),
      [
        sentinelTeam,
        isFleet,
        isDealerTeam,
        isDealerGroup,
        isSingleLocation,
        isTeamMember,
        belongsToOemSegment,
        hasSentinelTeam,
        isTeamEnabled,
        setCurrentSentinelTeam,
        logout,
      ],
    );

    if (setSentinelTeamLoading || isSettingTeam) {
      return <TeamChanger />;
    }

    if (loading) {
      return <Loading />;
    }

    return (
      <SentinelTeamContext.Provider value={values}>
        {children}
      </SentinelTeamContext.Provider>
    );
  },
);

SentinelTeamProvider.displayName = 'SentinelTeamProvider';

SentinelTeamProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const sentinelTeamProviderRef = createRef<SentinelTeamProviderHandles>();

export async function setSentinelTeam(opts?: SetSentinelTeamMutationOpts) {
  return sentinelTeamProviderRef?.current?.setSentinelTeam?.(opts);
}

export default SentinelTeamProvider;
